import React, { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Toast, ToastContainer } from "react-bootstrap";
import DetailsModal from "./DetailsModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import EditReportModal from "./EditReportModal";
import ScheduleTabContent from "./ScheduleTabContent";
import ViewTabContent from "./ViewTabContent";
import { fetchScheduleReports, submitScheduleReport } from "./api";
import {
  buildCreatePayload,
  buildUpdatePayload,
  defaultEditFormData,
  defaultFormState,
  defaultViewResult,
  findInvalidEmails,
  getCurrentUtcHourValue,
  parseRecipients,
  recipientsToString,
  validateCreateScheduleForm,
} from "./utils";

const ScheduleReport = () => {
  const [activeTab, setActiveTab] = useState("schedule");
  const [formData, setFormData] = useState({
    ...defaultFormState,
    execution_time: getCurrentUtcHourValue(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [showToast, setShowToast] = useState(false);

  const [viewCampaignId, setViewCampaignId] = useState("");
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [viewError, setViewError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [viewResult, setViewResult] = useState(defaultViewResult);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState(defaultEditFormData);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const auth = useAuth();
  const accessToken = auth.user?.id_token;
  const isRunNow = formData.frequency === "RUN NOW";
  const isEditRunNow = editFormData.frequency === "RUN NOW";

  const normalizeExecutionHour = (value) => {
    if (!value) return "";
    const match = String(value).match(/^(\d{1,2})/);
    if (!match) return "";
    const hour = Number(match[1]);
    if (!Number.isFinite(hour) || hour < 0 || hour > 23) return "";
    return String(hour);
  };

  const showStatusToast = (type, message) => {
    setStatus({ type, message });
    setShowToast(true);
  };

  const extractFieldValue = (input) => {
    if (!input?.target) return input;
    if (input.target.type === "checkbox") return input.target.checked;
    return input.target.value;
  };

  const handleChange = (field) => (input) => {
    setFormData((prev) => ({ ...prev, [field]: extractFieldValue(input) }));
  };

  const handleEditChange = (field) => (input) => {
    setEditFormData((prev) => ({ ...prev, [field]: extractFieldValue(input) }));
  };

  const loadViewReports = async (campaignId) => {
    setIsFetchingReports(true);
    try {
      const data = await fetchScheduleReports({ accessToken, campaignId });
      setViewResult({
        campaign_id: data.campaign_id || campaignId.trim(),
        total: data.total || 0,
        records: Array.isArray(data.records) ? data.records : [],
      });
      setViewError("");
    } catch (err) {
      setViewResult(defaultViewResult);
      setViewError(err.message);
    } finally {
      setIsFetchingReports(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowToast(false);

    const validationError = validateCreateScheduleForm(formData, isRunNow);
    if (validationError) {
      showStatusToast("error", validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const submittedCampaignId = formData.campaign_id.trim();
      const recipients = parseRecipients(formData.recipients);
      if (recipients.length === 0) {
        throw new Error("Please enter at least one valid recipient email.");
      }
      const invalidEmails = findInvalidEmails(recipients);
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid recipient email(s): ${invalidEmails.join(", ")}`);
      }

      const payload = buildCreatePayload(formData, isRunNow, recipients);
      const data = await submitScheduleReport({ accessToken, payload });
      const successMessage = data?.message || data?.body?.message || JSON.stringify(data);

      showStatusToast("success", successMessage || "Report scheduled successfully.");
      setViewCampaignId(submittedCampaignId);
      setHasSearched(true);
      setActiveTab("view");
      await loadViewReports(submittedCampaignId);
      setFormData({
        ...defaultFormState,
        execution_time: getCurrentUtcHourValue(),
      });
    } catch (err) {
      showStatusToast("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewSubmit = async (event) => {
    event.preventDefault();
    if (!viewCampaignId.trim()) {
      setViewError("Please enter a campaign ID.");
      return;
    }

    setHasSearched(true);
    await loadViewReports(viewCampaignId);
  };

  const openDetailsModal = (record) => {
    setSelectedRecord(record);
    setShowDetailsModal(true);
  };

  const openEditModal = (record) => {
    const normalizedExecutionHour = normalizeExecutionHour(record.execution_time);
    setEditFormData({
      id: record.id || "",
      campaign_id: record.campaign_id || "",
      report_name: record.report_name || "",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      execution_time: normalizedExecutionHour || getCurrentUtcHourValue(),
      frequency: record.frequency === "ONCE" ? "RUN NOW" : "DAILY",
      only_form_data: Boolean(record.only_form_data),
      recipients: recipientsToString(record.recipients),
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const handleUpdateReport = async (event) => {
    event.preventDefault();
    setShowToast(false);

    if (!editFormData.id) {
      showStatusToast("error", "Record id is missing for update.");
      return;
    }

    const validationError = validateCreateScheduleForm(editFormData, isEditRunNow);
    if (validationError) {
      showStatusToast("error", validationError);
      return;
    }

    setIsUpdating(true);
    try {
      const recipients = parseRecipients(editFormData.recipients);
      if (recipients.length === 0) {
        throw new Error("Please enter at least one valid recipient email.");
      }
      const invalidEmails = findInvalidEmails(recipients);
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid recipient email(s): ${invalidEmails.join(", ")}`);
      }

      const payload = buildUpdatePayload(editFormData, isEditRunNow, recipients);
      const data = await submitScheduleReport({ accessToken, payload });
      const successMessage = data?.message || data?.body?.message || JSON.stringify(data);

      showStatusToast("success", successMessage || "Report updated successfully.");
      setShowEditModal(false);

      if (viewCampaignId.trim()) {
        await loadViewReports(viewCampaignId);
      }
    } catch (err) {
      showStatusToast("error", err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!recordToDelete?.id) {
      showStatusToast("error", "Record id is missing for delete.");
      setShowDeleteModal(false);
      return;
    }

    setIsDeleting(true);
    try {
      const payload = {
        action: "delete",
        id: recordToDelete.id,
      };
      const data = await submitScheduleReport({ accessToken, payload });
      const successMessage = data?.message || data?.body?.message || JSON.stringify(data);

      showStatusToast("success", successMessage || "Report deleted successfully.");
      setShowDeleteModal(false);
      setRecordToDelete(null);

      if (viewCampaignId.trim()) {
        await loadViewReports(viewCampaignId);
      }
    } catch (err) {
      showStatusToast("error", err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-100 py-1">
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "schedule" ? "active" : ""}`}
            onClick={() => setActiveTab("schedule")}
          >
            Schedule
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Report
          </button>
        </li>
      </ul>

      {activeTab === "schedule" ? (
        <ScheduleTabContent
          formData={formData}
          isSubmitting={isSubmitting}
          isRunNow={isRunNow}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />
      ) : (
        <ViewTabContent
          viewCampaignId={viewCampaignId}
          setViewCampaignId={setViewCampaignId}
          onSearch={handleViewSubmit}
          isFetchingReports={isFetchingReports}
          viewError={viewError}
          viewResult={viewResult}
          hasSearched={hasSearched}
          onView={openDetailsModal}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />
      )}

      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={status.type === "success" ? "success" : "danger"}
          onClose={() => setShowToast(false)}
          show={showToast && !!status.message}
          delay={5000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">
              {status.type === "success" ? "Success" : "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{status.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <DetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        record={selectedRecord}
      />

      <EditReportModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        formData={editFormData}
        isUpdating={isUpdating}
        isRunNow={isEditRunNow}
        onChange={handleEditChange}
        onSubmit={handleUpdateReport}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteReport}
        isDeleting={isDeleting}
        reportName={recordToDelete?.report_name}
      />
    </div>
  );
};

export default ScheduleReport;
